import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardHeader, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import StarRating from '../components/StarRating';
import { Clock, DollarSign, Wallet, CheckCircle, AlertCircle, Inbox, Banknote } from 'lucide-react';
import api, { formatApiErrorDetail } from '../lib/api';

function QueryPoolCard({ query, onClaim }) {
  const [claiming, setClaiming] = useState(false);
  return (
    <Card className="border border-gray-200" data-testid={`pool-query-${query.id}`}>
      <CardContent className="pt-6">
        <div className="flex items-center space-x-2 mb-3">
          <Badge variant="outline">{query.queryType}</Badge>
          <Badge className={query.priority === 'Priority' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}>
            {query.priority} — ${query.price}
          </Badge>
          <Badge className="bg-green-100 text-green-800">Earn up to ${query.price / 2}</Badge>
        </div>
        <p className="text-gray-700 line-clamp-3 mb-3">{query.description}</p>
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500 flex items-center"><Clock className="h-4 w-4 mr-1" /> SLA: {query.responseTime}</p>
          <Button
            size="sm"
            disabled={claiming}
            onClick={async () => { setClaiming(true); await onClaim(query.id); setClaiming(false); }}
            className="bg-[#0078D4] hover:bg-[#005a9e]"
            data-testid={`claim-query-${query.id}`}
          >
            {claiming ? 'Grabbing...' : 'Grab Query'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function MyQueryCard({ query, onResolve }) {
  const [response, setResponse] = useState('');
  const [submitting, setSubmitting] = useState(false);
  return (
    <Card className="border border-gray-200" data-testid={`my-query-${query.id}`}>
      <CardContent className="pt-6">
        <div className="flex items-center space-x-2 mb-3">
          <Badge className={query.status === 'Resolved' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}>{query.status}</Badge>
          <Badge variant="outline">{query.queryType}</Badge>
          <Badge className="bg-gray-100 text-gray-800">${query.price}</Badge>
        </div>
        <p className="text-gray-700 whitespace-pre-wrap mb-3">{query.description}</p>
        <p className="text-sm text-gray-500 mb-3">From: {query.name}{query.companyName ? ` — ${query.companyName}` : ''} | SLA: {query.responseTime}</p>

        {query.status === 'In Progress' && (
          <div className="space-y-3 border-t pt-4">
            <Label className="font-medium text-gray-700">Your Solution</Label>
            <Textarea
              value={response}
              onChange={(e) => setResponse(e.target.value)}
              placeholder="Write your detailed solution for the customer..."
              className="min-h-[120px]"
              data-testid={`resolve-textarea-${query.id}`}
            />
            <Button
              disabled={submitting || !response.trim()}
              onClick={async () => { setSubmitting(true); await onResolve(query.id, response); setSubmitting(false); }}
              className="bg-green-600 hover:bg-green-700"
              data-testid={`resolve-submit-${query.id}`}
            >
              {submitting ? 'Submitting...' : 'Submit Resolution'}
            </Button>
          </div>
        )}

        {query.status === 'Resolved' && (
          <div className="border-t pt-4 space-y-3">
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-1">Your Response:</p>
              <p className="text-gray-600 text-sm whitespace-pre-wrap">{query.response}</p>
            </div>
            {query.feedback ? (
              <div className="bg-green-50 p-3 rounded-lg">
                <div className="flex items-center space-x-2 mb-1">
                  <StarRating value={query.feedback.rating} size={4} />
                  <span className="text-sm font-semibold text-green-800">Customer rated {query.feedback.rating}/5</span>
                </div>
                {query.feedback.comment && <p className="text-sm text-green-800 italic">"{query.feedback.comment}"</p>}
              </div>
            ) : (
              <p className="text-sm text-yellow-700 bg-yellow-50 p-2 rounded">Awaiting customer feedback — your payout is released once the customer rates the resolution.</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function ExpertDashboard() {
  const [profile, setProfile] = useState(null);
  const [pool, setPool] = useState([]);
  const [myQueries, setMyQueries] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [message, setMessage] = useState('');
  const [wForm, setWForm] = useState({ amount: '', bankName: '', accountName: '', accountNumber: '', ifscSwift: '' });
  const [wError, setWError] = useState('');
  const [wLoading, setWLoading] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const p = await api.get('/expert/profile');
      setProfile(p.data);
      const [q, e, w] = await Promise.all([
        api.get('/expert/queries'),
        api.get('/expert/earnings'),
        api.get('/expert/withdrawals'),
      ]);
      setMyQueries(q.data);
      setEarnings(e.data);
      setWithdrawals(w.data);
      if (p.data.status === 'verified') {
        const pl = await api.get('/expert/pool');
        setPool(pl.data);
      }
    } catch (err) {
      // profile load failure handled by blank state
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleClaim = async (id) => {
    try {
      await api.post(`/expert/pool/${id}/claim`);
      setMessage('Query claimed! Find it under "My Queries".');
      await loadAll();
    } catch (err) {
      setMessage(formatApiErrorDetail(err.response?.data?.detail));
      await loadAll();
    }
  };

  const handleResolve = async (id, response) => {
    try {
      await api.post(`/expert/queries/${id}/resolve`, { response });
      setMessage('Resolution submitted! Payout will be released after customer feedback.');
      await loadAll();
    } catch (err) {
      setMessage(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleWithdraw = async (e) => {
    e.preventDefault();
    setWError('');
    setWLoading(true);
    try {
      await api.post('/expert/withdrawals', { ...wForm, amount: parseFloat(wForm.amount) });
      setWForm({ amount: '', bankName: '', accountName: '', accountNumber: '', ifscSwift: '' });
      setMessage('Withdrawal requested! Solving365 will process it shortly.');
      await loadAll();
    } catch (err) {
      setWError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setWLoading(false);
    }
  };

  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex justify-center py-20 text-gray-500" data-testid="expert-loading">Loading your expert profile...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-[#003366] mb-1" data-testid="expert-dashboard-title">Expert Dashboard</h1>
          <p className="text-gray-600">{profile.headline}</p>
        </div>

        {profile.status === 'pending' && (
          <Alert className="mb-6 bg-yellow-50 border-yellow-200" data-testid="expert-pending-banner">
            <AlertCircle className="h-5 w-5 text-yellow-600" />
            <AlertDescription className="text-yellow-800">
              <strong>Your profile is pending verification by Solving365.</strong> You'll be able to grab customer queries once verified.
            </AlertDescription>
          </Alert>
        )}
        {profile.status === 'rejected' && (
          <Alert variant="destructive" className="mb-6" data-testid="expert-rejected-banner">
            <AlertCircle className="h-5 w-5" />
            <AlertDescription>
              <strong>Your application was not approved.</strong>{profile.rejectionReason ? ` Reason: ${profile.rejectionReason}` : ''} Contact phatak.kau@live.com for details.
            </AlertDescription>
          </Alert>
        )}
        {message && (
          <Alert className="mb-6 bg-blue-50 border-blue-200" data-testid="expert-message">
            <CheckCircle className="h-5 w-5 text-blue-600" />
            <AlertDescription className="text-blue-900">{message}</AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="pt-5">
              <p className="text-sm text-gray-600">Available Balance</p>
              <p className="text-2xl font-bold text-[#003366]" data-testid="expert-balance">${earnings ? earnings.availableBalance.toFixed(2) : '0.00'}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-5">
              <p className="text-sm text-gray-600">Total Earned</p>
              <p className="text-2xl font-bold text-[#003366]" data-testid="expert-total-earned">${earnings ? earnings.totalEarned.toFixed(2) : '0.00'}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-yellow-500">
            <CardContent className="pt-5">
              <p className="text-sm text-gray-600">Rating</p>
              <div className="flex items-center space-x-2">
                <p className="text-2xl font-bold text-[#003366]" data-testid="expert-rating">{profile.totalRatings > 0 ? profile.avgRating : '—'}</p>
                {profile.totalRatings > 0 && <StarRating value={Math.round(profile.avgRating)} size={4} />}
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="pt-5">
              <p className="text-sm text-gray-600">Queries Resolved</p>
              <p className="text-2xl font-bold text-[#003366]" data-testid="expert-resolved-count">{profile.resolvedCount}</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="pool" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="pool" data-testid="tab-pool">Available Queries ({pool.length})</TabsTrigger>
            <TabsTrigger value="mine" data-testid="tab-my-queries">My Queries ({myQueries.length})</TabsTrigger>
            <TabsTrigger value="earnings" data-testid="tab-earnings">Earnings & Withdrawals</TabsTrigger>
          </TabsList>

          <TabsContent value="pool">
            {profile.status !== 'verified' ? (
              <div className="text-center py-12 text-gray-500" data-testid="pool-locked">
                <Inbox className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                The query pool unlocks after your profile is verified.
              </div>
            ) : pool.length === 0 ? (
              <div className="text-center py-12 text-gray-500" data-testid="pool-empty">
                <Inbox className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                No unclaimed paid queries right now. Check back soon!
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {pool.map((q) => <QueryPoolCard key={q.id} query={q} onClaim={handleClaim} />)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="mine">
            {myQueries.length === 0 ? (
              <div className="text-center py-12 text-gray-500" data-testid="my-queries-empty">You haven't claimed any queries yet.</div>
            ) : (
              <div className="space-y-6">
                {myQueries.map((q) => <MyQueryCard key={q.id} query={q} onResolve={handleResolve} />)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="earnings">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader><h3 className="font-bold text-[#003366] flex items-center"><Banknote className="h-5 w-5 mr-2" />Request Withdrawal</h3></CardHeader>
                <CardContent>
                  {wError && (
                    <Alert variant="destructive" className="mb-4" data-testid="withdraw-error">
                      <AlertCircle className="h-4 w-4" /><AlertDescription>{wError}</AlertDescription>
                    </Alert>
                  )}
                  <form onSubmit={handleWithdraw} className="space-y-4">
                    <div>
                      <Label>Amount (USD)</Label>
                      <Input type="number" step="0.01" min="1" required value={wForm.amount}
                        onChange={(e) => setWForm({ ...wForm, amount: e.target.value })} className="mt-1" data-testid="withdraw-amount-input" />
                      <p className="text-xs text-gray-500 mt-1">Available: ${earnings ? earnings.availableBalance.toFixed(2) : '0.00'}</p>
                    </div>
                    <div>
                      <Label>Bank Name</Label>
                      <Input required value={wForm.bankName} onChange={(e) => setWForm({ ...wForm, bankName: e.target.value })} className="mt-1" data-testid="withdraw-bank-input" />
                    </div>
                    <div>
                      <Label>Account Holder Name</Label>
                      <Input required value={wForm.accountName} onChange={(e) => setWForm({ ...wForm, accountName: e.target.value })} className="mt-1" data-testid="withdraw-account-name-input" />
                    </div>
                    <div>
                      <Label>Account Number</Label>
                      <Input required value={wForm.accountNumber} onChange={(e) => setWForm({ ...wForm, accountNumber: e.target.value })} className="mt-1" data-testid="withdraw-account-number-input" />
                    </div>
                    <div>
                      <Label>IFSC / SWIFT Code</Label>
                      <Input value={wForm.ifscSwift} onChange={(e) => setWForm({ ...wForm, ifscSwift: e.target.value })} className="mt-1" data-testid="withdraw-ifsc-input" />
                    </div>
                    <Button type="submit" disabled={wLoading || profile.status !== 'verified'} className="w-full bg-[#0078D4] hover:bg-[#005a9e]" data-testid="withdraw-submit">
                      {wLoading ? 'Requesting...' : 'Request Withdrawal'}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              <div className="space-y-6">
                <Card>
                  <CardHeader><h3 className="font-bold text-[#003366] flex items-center"><DollarSign className="h-5 w-5 mr-2" />Earnings History</h3></CardHeader>
                  <CardContent>
                    {!earnings || earnings.earnings.length === 0 ? (
                      <p className="text-sm text-gray-500" data-testid="earnings-empty">No earnings yet. Resolve queries and get rated to earn.</p>
                    ) : (
                      <div className="space-y-2">
                        {earnings.earnings.map((e) => (
                          <div key={e.id} className="flex justify-between items-center border-b pb-2 text-sm" data-testid={`earning-${e.id}`}>
                            <div>
                              <p className="font-medium text-gray-900">${e.queryPrice} query — {e.rating}★ ({e.percent}%)</p>
                              <p className="text-xs text-gray-500">{new Date(e.createdAt).toLocaleDateString()}</p>
                            </div>
                            <p className={`font-bold ${e.amount > 0 ? 'text-green-600' : 'text-gray-400'}`}>+${e.amount.toFixed(2)}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader><h3 className="font-bold text-[#003366] flex items-center"><Wallet className="h-5 w-5 mr-2" />Withdrawal History</h3></CardHeader>
                  <CardContent>
                    {withdrawals.length === 0 ? (
                      <p className="text-sm text-gray-500" data-testid="withdrawals-empty">No withdrawal requests yet.</p>
                    ) : (
                      <div className="space-y-2">
                        {withdrawals.map((w) => (
                          <div key={w.id} className="flex justify-between items-center border-b pb-2 text-sm" data-testid={`withdrawal-${w.id}`}>
                            <div>
                              <p className="font-medium text-gray-900">${w.amount.toFixed(2)} — {w.bankName}</p>
                              <p className="text-xs text-gray-500">{new Date(w.requestedAt).toLocaleDateString()}</p>
                            </div>
                            <Badge className={w.status === 'paid' ? 'bg-green-100 text-green-800' : w.status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}>
                              {w.status}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
      <Footer />
    </div>
  );
}
